import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowUpRight, Phone, Mail, MessageCircle, Instagram, Linkedin, Facebook, Twitter } from "lucide-react";
import { brand, nav } from "@/data/content";

const EASE = [0.76, 0, 0.24, 1];

const socials = [
  { icon: Instagram, href: brand.social.instagram, label: "Instagram" },
  { icon: Linkedin, href: brand.social.linkedin, label: "LinkedIn" },
  { icon: Facebook, href: brand.social.facebook, label: "Facebook" },
  { icon: Twitter, href: brand.social.twitter, label: "Twitter" },
];

export default function MenuDrawer({ open, onClose }) {
  useEffect(() => {
    document.documentElement.classList.toggle("drawer-open", open);
    return () => document.documentElement.classList.remove("drawer-open");
  }, [open]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="drawer-backdrop"
            data-lenis-prevent
            className="fixed inset-0 z-[9990] bg-ink/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            onClick={onClose}
          />
          <motion.aside
            data-testid="menu-drawer"
            data-lenis-prevent
            className="fixed inset-0 z-[9995] flex h-[100dvh] w-screen flex-col overflow-y-auto overscroll-contain bg-ink text-bone"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.65, ease: EASE }}
            aria-label="Site menu"
          >
            <div className="flex items-center justify-between px-6 pt-6 sm:px-8 sm:pt-7 md:px-10 md:pt-9 lg:px-12 xl:px-16">
              <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-bone/50">
                Menu
              </span>
              <button
                data-testid="drawer-close"
                aria-label="Close menu"
                onClick={onClose}
                className="group flex h-11 w-11 items-center justify-center border border-bone/20 transition-colors duration-300 hover:border-accent hover:bg-accent"
              >
                <X size={20} className="transition-transform duration-300 group-hover:rotate-90" />
              </button>
            </div>

            <nav className="mt-8 px-6 sm:mt-10 sm:px-8 md:px-10 lg:px-12 xl:px-16">
              <ul>
                {nav.map((item, i) => (
                  <li key={item.to} className="overflow-hidden border-b border-bone/10">
                    <motion.div
                      initial={{ y: "110%" }}
                      animate={{ y: 0 }}
                      exit={{ y: "110%" }}
                      transition={{ duration: 0.6, delay: 0.15 + i * 0.06, ease: EASE }}
                    >
                      <NavLink
                        to={item.to}
                        data-testid={`drawer-nav-${item.label.toLowerCase()}`}
                        onClick={onClose}
                        className={({ isActive }) =>
                          `group flex items-baseline justify-between py-4 transition-colors duration-300 ${
                            isActive ? "text-accent" : "text-bone hover:text-accent"
                          }`
                        }
                      >
                        <span className="flex items-baseline gap-4">
                          <span className="text-[10px] font-bold tabular-nums text-bone/40">
                            0{i + 1}
                          </span>
                          <span className="font-display text-4xl font-black tracking-tight transition-transform duration-300 group-hover:translate-x-2 md:text-5xl">
                            {item.label}
                          </span>
                        </span>
                        <ArrowUpRight
                          size={22}
                          className="opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                        />
                      </NavLink>
                    </motion.div>
                  </li>
                ))}
              </ul>
            </nav>

            <motion.div
              className="mt-auto px-6 pb-8 pt-10 sm:px-8 sm:pt-12 md:px-10 md:pb-10 lg:px-12 xl:px-16"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, delay: 0.5, ease: EASE }}
            >
              <p className="mb-4 text-[10px] font-bold uppercase tracking-[0.3em] text-bone/40">
                Get in touch
              </p>
              <div className="flex flex-col gap-3">
                <a
                  data-testid="drawer-phone"
                  href={brand.phoneHref}
                  className="link-underline inline-flex w-fit items-center gap-3 text-sm font-semibold text-bone/80 hover:text-accent"
                >
                  <Phone size={15} /> {brand.phone}
                </a>
                <a
                  data-testid="drawer-email"
                  href={`mailto:${brand.email}`}
                  className="link-underline inline-flex w-fit items-center gap-3 text-sm font-semibold text-bone/80 hover:text-accent"
                >
                  <Mail size={15} /> {brand.email}
                </a>
                <a
                  data-testid="drawer-whatsapp"
                  href={`https://wa.me/${brand.whatsapp}`}
                  target="_blank"
                  rel="noreferrer"
                  className="link-underline inline-flex w-fit items-center gap-3 text-sm font-semibold text-bone/80 hover:text-accent"
                >
                  <MessageCircle size={15} /> WhatsApp us
                </a>
              </div>

              <div className="mt-8 flex items-center justify-between border-t border-bone/10 pt-6">
                <div className="flex gap-3">
                  {socials.map(({ icon: Icon, href, label }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={label}
                      data-testid={`drawer-social-${label.toLowerCase()}`}
                      className="flex h-10 w-10 items-center justify-center border border-bone/15 text-bone/70 transition-colors duration-300 hover:border-accent hover:bg-accent hover:text-bone"
                    >
                      <Icon size={16} />
                    </a>
                  ))}
                </div>
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-bone/40">
                  {brand.city}
                </span>
              </div>

              <Link
                to="/contact"
                data-testid="drawer-cta"
                onClick={onClose}
                className="mt-7 block w-full bg-accent px-6 py-4 text-center text-xs font-bold uppercase tracking-[0.18em] text-bone transition-colors duration-300 hover:bg-bone hover:text-ink"
              >
                Discuss a Project
              </Link>
            </motion.div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
